/**
 * Composant pour afficher le message de succès
 * @returns 
 */
import { Check, Clock, Timer, User } from "lucide-react";

interface SuccessMessageProps {
  action: "CLOCK_IN" | "CLOCK_OUT";
  employeeName: string;
  duration?: number | null;
  time: string;
  onClose: () => void;
}

export const SuccessMessage = ({
  action,
  employeeName,
  duration,
  time,
  onClose,
}: SuccessMessageProps) => {
  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);

    if (h === 0) return `${m} minute${m > 1 ? "s" : ""}`;
    if (m === 0) return `${h} heure${h > 1 ? "s" : ""}`;

    return `${h}h${m.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        onClick={onClose}
      >
        <div className="p-8 text-center space-y-6">
          {/* Success animation */}
          <div className="relative mx-auto w-24 h-24">
            <div className="absolute inset-0 rounded-full bg-green-500/20 animate-pulse-ring" />
            <div
              className="absolute inset-2 rounded-full bg-green-500/30 animate-pulse-ring"
              style={{ animationDelay: "0.3s" }}
            />
            <div className="relative w-24 h-24 rounded-full bg-green-500 flex items-center justify-center">
              <Check
                className="w-12 h-12 text-white animate-in zoom-in-95 duration-300"
                strokeWidth={3}
                style={{ animationDelay: "0.2s" }}
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {action === "CLOCK_IN"
                ? `Pointage enregistré`
                : `Session terminée`}
            </h2>
            <p className="text-muted-foreground">
              {action === "CLOCK_IN"
                ? `Bonne journée de travail !`
                : `À bientôt !`}
            </p>
          </div>

          {/* Employee details */}
          <div className="bg-muted rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-center gap-2 text-foreground">
              <User className="w-5 h-5 text-primary" />
              <span className="font-medium">{employeeName}</span>
            </div>

            {action === "CLOCK_OUT" &&
              duration !== null &&
              duration !== undefined && (
                <div className="flex items-center justify-center gap-2 text-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="font-medium">
                    Durée: {formatDuration(duration)}
                  </span>
                </div>
              )}
          </div>

          {/* Time */}
          <p className="text-sm text-muted-foreground">
            {action === "CLOCK_IN" ? "Arrivée" : "Départ"} à {time}
          </p>

          {/* Tap to close hint */}
          <p className="text-xs text-muted-foreground/60 cursor-pointer">
            Appuyez pour fermer
          </p>
        </div>
      </div>
    </div>
  );
};
